import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { adminApi, getAdminToken, setAdminToken } from "../lib/api";
import {
  LogOut,
  Mail,
  Phone,
  Search,
  RefreshCw,
  Inbox,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import FSSLogo from "../components/site/FSSLogo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/leads");
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken("");
        navigate("/admin");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) =>
      [l.name, l.company, l.email, l.service, l.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(s))
    );
  }, [q, leads]);

  const stats = useMemo(() => {
    const total = leads.length;
    const today = leads.filter((l) => {
      const d = new Date(l.created_at);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    }).length;
    const newCount = leads.filter((l) => l.status === "new").length;
    const sent = leads.filter((l) => l.email_sent).length;
    return { total, today, newCount, sent };
  }, [leads]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/leads/${id}/status?status=${status}`);
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      if (active?.id === id) setActive({ ...active, status });
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const logout = () => {
    setAdminToken("");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-leads-page">
      <header className="bg-white border-b border-slate-200">
        <div className="fss-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <FSSLogo />
            <div className="hidden md:block h-8 w-px bg-slate-200" />
            <div className="hidden md:block">
              <div className="text-xs font-bold tracking-[0.22em] uppercase text-slate-500">
                Admin
              </div>
              <div className="font-display font-semibold text-slate-900 text-sm">
                Leads Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="fss-btn fss-btn-outline h-9 px-4 text-xs"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Refresh
            </button>
            <button
              onClick={logout}
              className="fss-btn fss-btn-dark h-9 px-4 text-xs"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="fss-container py-8 flex-1">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-6">
          <Stat label="Total leads" value={stats.total} />
          <Stat label="Today" value={stats.today} />
          <Stat label="New" value={stats.newCount} accent="green" />
          <Stat label="Email sent" value={stats.sent} accent="red" />
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search leads..."
              className="fss-input pl-10"
              data-testid="admin-search"
            />
          </div>
          <div className="text-xs text-slate-500 ml-auto">
            Showing <span className="font-semibold text-slate-900">{filtered.length}</span> of {leads.length}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {loading ? (
            <div className="py-20 grid place-items-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mb-3" />
              Loading leads...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 grid place-items-center text-center px-6">
              <Inbox className="w-10 h-10 text-slate-400 mb-4" />
              <h3 className="font-display text-lg font-semibold text-slate-900">
                No leads yet
              </h3>
              <p className="mt-1 text-sm text-slate-500 max-w-md">
                When prospects submit the contact form on the website, their enquiries will appear here.
              </p>
              <Link to="/" className="mt-4 fss-btn fss-btn-outline h-9 text-xs">
                Visit website
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow
                    key={l.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setActive(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleDateString("en-IE", {
                        day: "2-digit",
                        month: "short",
                        year: "2-digit",
                      })}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-900">{l.name}</TableCell>
                    <TableCell className="text-slate-600">{l.company || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{l.service}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-3">
                        <a
                          href={`mailto:${l.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-700 hover:text-green-700"
                        >
                          <Mail className="w-4 h-4" />
                        </a>
                        {l.phone && (
                          <a
                            href={`tel:${l.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-700 hover:text-green-700"
                          >
                            <Phone className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={l.status} />
                    </TableCell>
                    <TableCell>
                      {l.email_sent ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-sm">Sent</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 rounded-sm">DB only</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {active && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex justify-end"
          onClick={() => setActive(null)}
          data-testid="lead-detail-drawer"
        >
          <div
            className="bg-white w-full max-w-lg h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="fss-overline">Lead Detail</div>
                <h2 className="mt-1 font-display text-xl font-semibold text-slate-900">
                  {active.name}
                </h2>
              </div>
              <button
                onClick={() => setActive(null)}
                className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-5">
              <DetailRow label="Service" value={active.service} />
              <DetailRow label="Company" value={active.company || "—"} />
              <DetailRow label="Site Location" value={active.site_location || "—"} />
              <DetailRow label="Number of Sites" value={active.number_of_sites || "—"} />
              <DetailRow label="New / Existing System" value={active.existing_system || "—"} />
              <DetailRow label="Preferred Contact" value={active.preferred_contact || "—"} />
              <DetailRow
                label="Email"
                value={
                  <a className="text-green-700 font-semibold" href={`mailto:${active.email}`}>
                    {active.email}
                  </a>
                }
              />
              <DetailRow
                label="Phone"
                value={
                  active.phone ? (
                    <a className="text-green-700 font-semibold" href={`tel:${active.phone}`}>
                      {active.phone}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <DetailRow label="Submitted" value={new Date(active.created_at).toLocaleString("en-IE")} />
              <div>
                <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-2">
                  Message
                </div>
                <div className="bg-slate-50 border-l-2 border-red-600 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {active.message}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-2">
                  Status
                </div>
                <div className="flex flex-wrap gap-2">
                  {["new", "contacted", "qualified", "closed"].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(active.id, s)}
                      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded-sm border transition-colors ${
                        active.status === s
                          ? "bg-green-700 text-white border-green-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-green-700 hover:text-green-700"
                      }`}
                      data-testid={`status-btn-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color =
    accent === "green" ? "text-green-700" : accent === "red" ? "text-red-600" : "text-slate-900";
  return (
    <div className="bg-white p-5">
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
        {label}
      </div>
      <div className={`mt-2 font-display text-3xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 items-start">
      <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
        {label}
      </div>
      <div className="col-span-2 text-sm text-slate-900">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-red-100 text-red-700 hover:bg-red-100",
    contacted: "bg-amber-100 text-amber-800 hover:bg-amber-100",
    qualified: "bg-blue-100 text-blue-800 hover:bg-blue-100",
    closed: "bg-slate-200 text-slate-700 hover:bg-slate-200",
  };
  return (
    <Badge className={`${map[status] || map.new} rounded-sm uppercase text-[10px] tracking-wide`}>
      {status}
    </Badge>
  );
}
